import React, { useState } from "react";
import BaseTextField from "../../components/ui/BaseTextField";
import * as yup from "yup";
import { useFormik } from "formik";
import BaseButton from "../../components/ui/BaseButton";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { resetPassword } from "../../services/api/auth";
import { useMutation } from "@tanstack/react-query";
import { showToast } from "../../store/toast/toastSlice";

import { useDispatch } from "react-redux";
import PasswordChecker from "../../components/ui/PasswordChecker";

const validationSchema = yup.object({
  newPassword: yup
    .string()
    .required("New Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(/[!@#$%^&*]/, "Password must contain at least one symbol")
    .test(
      "no-spaces",
      "Password cannot contain spaces",
      (value) => !/\s/.test(value), // Rejects passwords with spaces
    ),
  confirmPassword: yup
    .string()
    .required("Confirm Password is required")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

const ResetPasswordForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isTokenError, setIsTokenError] = useState(false);
  const [isShowNewPassword, setIsShowNewPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  const {
    isPending: isResetPasswordLoading,
    error: isResetPasswordError,
    mutateAsync: resetPasswordMutation,
  } = useMutation({
    mutationFn: resetPassword,
  });
  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      console.log(values);
      let payload = {
        newPassword: values.newPassword,
        resetToken: token,
      };
      resetPasswordButtonClicked(payload);
    },
    validateOnChange: true,
    validateOnBlur: false,
  });

  const resetPasswordButtonClicked = async (payload) => {
    try {
      const resetPasswordResponse = await resetPasswordMutation(payload);

      dispatch(
        showToast({
          style: {
            width: "350px",
          },
          position: "top-center",
          message: resetPasswordResponse?.data?.message,
        }),
      );

      formik.resetForm();
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      setIsTokenError(true);
      dispatch(
        showToast({
          style: {
            width: "350px",
          },
          position: "top-center",
          message: error?.response?.data?.message,
        }),
      );
    }
  };

  return (
    <div className="bg-white rounded-lg py-8 w-full">
      <h4 className="font-semibold text-center mb-2 text-[#525354]">Reset Password</h4>
      <p className="text-base text-center mb-10">Please Enter your new password</p>
      <BaseTextField
        className="mb-2"
        startAdornment={<i className="cursor-default fa-solid  text-base"></i>}
        endAdornment={
          <i
            className={`fa-solid ${
              isShowNewPassword ? "fa-eye-slash" : "fa-eye"
            } text-base cursor-pointer text-gray-400 `}
            onClick={() => setIsShowNewPassword(!isShowNewPassword)}
          ></i>
        }
        height="60px"
        labelShrink={true}
        type={isShowNewPassword ? "text" : "password"}
        label="New Password *"
        id="newPassword"
        name="newPassword"
        placeholder="**************"
        value={formik.values.newPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
        helperText={formik.touched.newPassword && formik.errors.newPassword}
      />
      <div className="my-6">
        <BaseTextField
          startAdornment={<i className="cursor-default fa-solid text-base"></i>}
          endAdornment={
            <i
              className={`fa-solid ${
                isShowNewPassword ? "fa-eye-slash" : "fa-eye"
              } text-base cursor-pointer text-gray-400 `}
              onClick={() => setIsShowConfirmPassword(!isShowConfirmPassword)}
            ></i>
          }
          height="60px"
          labelShrink={true}
          type={isShowConfirmPassword ? "text" : "password"}
          label="Confirm Password *"
          id="confirmPassword"
          name="confirmPassword"
          placeholder="**************"
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
          helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
        />
      </div>
      <PasswordChecker password={formik.values.newPassword} />
      <BaseButton
        color="primary"
        isLoading={isResetPasswordLoading}
        loaderSize="22px"
        type="submit"
        className="mt-8 text-base py-[8px] rounded-full"
        fullWidth={true}
        onClick={formik.handleSubmit}
      >
        Create New Password
      </BaseButton>

      {isTokenError && (
        <Link to="/forgotPassword">
          <p className="text-secondary text-center !text-primary mt-4 font-medium">
            Back to Forgot Password
          </p>
        </Link>
      )}
    </div>
  );
};

export default ResetPasswordForm;
